import React, { useContext, useEffect, useState } from 'react';
import Cookie from 'js-cookie';
import { useLocation } from 'react-router-dom';
import { SuccessCancelModal } from '../modals';
import { Footnote } from '@qover/typography';
import { FormattedMarkdown } from '@qover/react-intl';
import { Select, SecondaryGhostButton } from '@qover/molecules';
import CloseIcon from '@qover/icons/CloseIcon';
import { countryCodeToFlagEmoji } from '@qover/utils/src';
import { Trans } from '@lingui/macro';
import {
  ActionContainer,
  CloseContainer,
  Container,
  InnerContainer,
  Form,
} from './countrySelector.styles';
import { LocaleContext, RefsContext } from '@qover/contexts';
import { I18n, withI18n, withI18nProps } from '@lingui/react';

type CountrySelectProps = {
  flowStartUrl: string;
  updateDraftCountry?: (locale: string) => Promise<any>;
  showCountrySelectorModal?: boolean;
};

export function CountrySelect(props: CountrySelectProps) {
  const location = useLocation();
  const {
    changeLocale,
    country,
    countries,
    locales,
    paramKey,
    locale,
  } = useContext(LocaleContext);
  const [nextLocale, setNextLocale] = useState('');

  function onSuccess(newLocale?: string) {
    const locStr = newLocale || nextLocale;
    if (paramKey) {
      Cookie.set('language', locStr, { expires: 9999 });
      const search = new URLSearchParams(location.search);
      search.set(paramKey, locStr);
      if (props.updateDraftCountry) {
        const promise = search.get('id')
          ? props.updateDraftCountry(locStr)
          : Promise.resolve();
        promise.then(() => {
          window.location.assign(`${props.flowStartUrl}?${search.toString()}`);
        });
      }
    }
  }

  function handleChange(newLocale: string) {
    if (country !== newLocale.substring(3) && paramKey) {
      if (props.showCountrySelectorModal) {
        setNextLocale(newLocale);
      } else {
        onSuccess(newLocale);
      }
    } else {
      changeLocale(newLocale, true);
    }
  }

  if (!countries.length) {
    return null;
  }

  return (
    <>
      <I18n>
        {({ i18n }) => (
          <Select
            className="select"
            options={locales.map(l => ({
              value: l,
              label: i18n._({
                id: `general.localesWithFlag.${l}`,
                values: { flag: countryCodeToFlagEmoji(l.substring(3)) },
              }),
            }))}
            onChange={e => handleChange(e.target.value)}
            value={locale.value}
          />
        )}
      </I18n>
      {nextLocale && (
        <SuccessCancelModal
          description={<Trans id="general.countryChange.description" />}
          onCancelClick={() => setNextLocale('')}
          onSuccessClick={() => onSuccess()}
          title={<Trans id="general.countryChange" />}
        />
      )}
    </>
  );
}

export function CountrySelectorForm(props: CountrySelectProps) {
  const location = useLocation();
  const {
    changeLocale,
    country,
    countries,
    locale,
    locales,
    paramKey,
  } = useContext(LocaleContext);
  const [localeValue, setLocaleValue] = useState(locale.value);
  const [nextLocale, setNextLocale] = useState('');

  useEffect(() => {
    setLocaleValue(locale.value);
  }, [locale.value]);

  function onSuccess(newLocale?: string) {
    const locStr = newLocale || nextLocale;
    if (paramKey) {
      Cookie.set('language', locStr, { expires: 9999 });
      Cookie.set('user-defined-locale', 'true', { expires: 9999 });
      const search = new URLSearchParams(location.search);
      search.set(paramKey, locStr);
      if (props.updateDraftCountry) {
        const promise = search.get('id')
          ? props.updateDraftCountry(locStr)
          : Promise.resolve();
        promise.then(() => {
          window.location.assign(`${props.flowStartUrl}?${search.toString()}`);
        });
      }
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (country !== localeValue.substring(3) && paramKey) {
      if (props.showCountrySelectorModal) {
        setNextLocale(localeValue);
      } else {
        onSuccess(localeValue);
      }
    } else {
      changeLocale(localeValue, true);
    }
  }

  if (!countries.length) {
    return null;
  }

  return (
    <>
      <Form onSubmit={handleSubmit} data-test="country-selector-form">
        <I18n>
          {({ i18n }) => (
            <Select
              className="select"
              options={locales.map(l => ({
                value: l,
                label: i18n._({
                  id: `general.localesWithFlag.${l}`,
                  values: { flag: countryCodeToFlagEmoji(l.substring(3)) },
                }),
              }))}
              onChange={e => setLocaleValue(e.target.value)}
              value={localeValue}
            />
          )}
        </I18n>
        <ActionContainer className="action">
          <SecondaryGhostButton small type="submit">
            <Trans id="general.countrySelector.button" />
          </SecondaryGhostButton>
        </ActionContainer>
      </Form>
      {nextLocale && (
        <SuccessCancelModal
          description={<Trans id="general.countryChange.description" />}
          onCancelClick={() => setNextLocale('')}
          onSuccessClick={() => onSuccess()}
          title={<Trans id="general.countryChange" />}
        />
      )}
    </>
  );
}

type BaseCountrySelectorProps = CountrySelectProps & withI18nProps;

function BaseCountrySelector(props: BaseCountrySelectorProps) {
  const { country } = useContext(RefsContext);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (closed) {
      Cookie.set('user-defined-locale', 'true', { expires: 9999 });
    }
  }, [closed]);

  if (Cookie.get('user-defined-locale') === 'true' || closed) {
    return null;
  }

  return (
    <Container>
      <InnerContainer>
        <Footnote as="div" className="text">
          <FormattedMarkdown
            id="general.countrySelector.description"
            defaults="Vous êtes actuellement sur le site {country} de Qover-me.
            Choisissez dans la liste votre pays de résidence si celui-ci n’est pas correct."
            values={{
              country: props.i18n._({ id: `countries.adjectives.${country}` }),
            }}
          />
        </Footnote>
        <CountrySelectorForm {...props} />
        <CloseContainer onClick={() => setClosed(true)}>
          <CloseIcon />
        </CloseContainer>
      </InnerContainer>
    </Container>
  );
}

export const CountrySelector = withI18n()(BaseCountrySelector);
